'use client';

import Icon from '@/components/ui/AppIcon';

export default function VisionSection() {
  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-surface via-background to-brand-primary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary/10 rounded-full mb-6">
            <Icon name="EyeIcon" size={20} className="text-brand-primary" variant="solid" />
            <span className="text-sm font-semibold text-brand-primary">Our Vision</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            Vision
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            We are building toward a future where food deserts do not exist. Our work is guided by a long-term strategic roadmap with clear milestones and measurable outcomes that reflect our commitment to empowering food-insecure communities with resilient and reliable resources. Through focused action and accountability, we are shaping a model for localized food access, education, and innovation that can scale sustainably over time.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-brand-lg p-8 lg:p-12 mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-brand-primary/10 rounded-xl flex items-center justify-center">
                  <Icon name="RocketLaunchIcon" size={32} className="text-brand-primary" variant="solid" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-text-primary">Scaling Impact Nationwide</h3>
                </div>
              </div>
              
              <p className="text-lg text-text-secondary mb-6 leading-relaxed">
                We envision Food for Thought programs embedded in schools and communities across the United States, serving as local hubs for food production, education, and empowerment. By expanding beyond pilot programs, we aim to reach communities annually with hands-on learning experiences and reliable access to nutritious food.
              </p>
              
              <p className="text-base font-semibold text-text-primary mb-4">Impact we measure includes:</p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="CheckIcon" size={16} className="text-success" variant="solid" />
                  </div>
                  <span className="text-text-secondary">Partner schools and community sites nationwide</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="CheckIcon" size={16} className="text-success" variant="solid" />
                  </div>
                  <span className="text-text-secondary">Individuals and students engaged annually</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="CheckIcon" size={16} className="text-success" variant="solid" />
                  </div>
                  <span className="text-text-secondary">Pounds of fresh produce grown and distributed</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="CheckIcon" size={16} className="text-success" variant="solid" />
                  </div>
                  <span className="text-text-secondary">Certified student chapters and community ambassadors</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-brand-primary/5 to-accent/5 rounded-xl p-8">
              <h4 className="text-xl font-bold text-text-primary mb-6">Strategic Priorities</h4>
              
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-brand-sm hover:shadow-brand-md transition-all duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon name="ChartBarIcon" size={24} className="text-brand-primary" variant="solid" />
                    <h5 className="font-bold text-text-primary">Nourish</h5>
                  </div>
                  <p className="text-sm text-text-secondary">Alleviate food insecurity by providing high-quality produce through Food for Thought programs that create localized food production and strengthen community access.</p>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-brand-sm hover:shadow-brand-md transition-all duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon name="LightBulbIcon" size={24} className="text-accent" variant="solid" />
                    <h5 className="font-bold text-text-primary">Educate</h5>
                  </div>
                  <p className="text-sm text-text-secondary">Empower communities with knowledge and skills in nutrition, food science, and food systems through accessible curriculum that builds resilient food systems.</p>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-brand-sm hover:shadow-brand-md transition-all duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon name="UserGroupIcon" size={24} className="text-success" variant="solid" />
                    <h5 className="font-bold text-text-primary">Innovate</h5>
                  </div>
                  <p className="text-sm text-text-secondary">Disrupt autonomous farming with localized food production technology that serves educational and nourishment purposes, prioritizing communities most impacted by food insecurity.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Strategic Impact Plan Section - Added spacing */}
        <div className="mt-16 pt-16 border-t border-border">
          <div className="text-center">
            <h3 className="text-2xl sm:text-3xl font-bold text-text-primary mb-6">
              Strategic Impact Plan
            </h3>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-8">
              Our comprehensive roadmap for achieving measurable, sustainable impact in youth climate education.
            </p>
            <a
              href="https://drive.google.com/file/d/1rPkfyPHAdqdKKYnMYdOaTg8Ggop-NRNC/view?usp=sharing"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-primary text-brand-primary-foreground font-semibold rounded-xl hover:bg-brand-primary/90 hover:shadow-brand-lg hover:-translate-y-1 transition-all duration-300">

              <Icon name="DocumentTextIcon" size={20} variant="solid" />
              <span>Download Strategic Plan</span>
            </a>
          </div>
        </div>
      </div>
    </section>);

}